/**
 * Sales Data Redux Slice
 * Manages sales data for events from SeatData API
 */

import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { getSalesDataByEvent } from '../util/api/supabaseApi';

// Cache TTL: 5 minutes for sales data (longer than listings since sales data changes less frequently)
const SALES_DATA_CACHE_TTL = 5 * 60 * 1000;

// Async thunks
export const fetchSalesDataByEvent = createAsyncThunk(
  'salesData/fetchByEvent',
  async (eventId, { rejectWithValue, getState }) => {
    try {
      const state = getState();
      const cached = state.salesData?.byEventId?.[eventId];

      // Return cached if fresh
      if (cached && cached.lastFetched && Date.now() - cached.lastFetched < SALES_DATA_CACHE_TTL) {
        const salesData = cached.salesDataIds
          .map(id => state.salesData?.byId?.[id])
          .filter(Boolean);

        if (salesData.length > 0) {
          return { eventId, salesData };
        }
      }

      const salesData = await getSalesDataByEvent(eventId);
      return { eventId, salesData };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Slice
const salesDataSlice = createSlice({
  name: 'salesData',
  initialState: {
    byId: {}, // Normalized: { [salesDataId]: salesData }
    byEventId: {}, // { [eventId]: { salesDataIds: [], lastFetched: number } }
    loading: false,
    error: null,
  },
  reducers: {
    clearSalesData: state => {
      state.byEventId = {};
      state.byId = {};
    },
    // Clean expired cache entries
    cleanExpiredCache: state => {
      const now = Date.now();
      Object.keys(state.byEventId).forEach(eventId => {
        const cached = state.byEventId[eventId];
        if (cached.lastFetched && now - cached.lastFetched > SALES_DATA_CACHE_TTL) {
          // Keep the data but mark as stale (or remove if you prefer)
          delete cached.lastFetched;
        }
      });
    },
  },
  extraReducers: builder => {
    builder
      .addCase(fetchSalesDataByEvent.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSalesDataByEvent.fulfilled, (state, action) => {
        state.loading = false;
        const { eventId, salesData } = action.payload;

        // Normalize: store sales data by ID
        if (salesData && salesData.length > 0) {
          salesData.forEach((sale, idx) => {
            // Use sale.id or generate a unique key
            const saleId = sale.id || sale.sale_id || `${eventId}-sale-${idx}`;
            state.byId[saleId] = { ...sale, id: saleId };
          });
        }

        // Store relationship
        state.byEventId[eventId] = {
          salesDataIds:
            salesData?.map((sale, idx) => sale.id || sale.sale_id || `${eventId}-sale-${idx}`) ||
            [],
          lastFetched: Date.now(),
        };
      })
      .addCase(fetchSalesDataByEvent.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const clearSalesData = salesDataSlice.actions.clearSalesData;
export const cleanExpiredCache = salesDataSlice.actions.cleanExpiredCache;
export default salesDataSlice.reducer;
